import { computed, inject, Injectable, isDevMode } from '@angular/core';
import { WINDOW } from '@innogy/utils-dom';
import type {
  WebchatInstance,
  WebchatOptions,
} from '@conversational/chat-models';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { MediaObserverService } from '@innogy/shared/responsive-tools';
import { PlatformService } from '@core/platform';
import { ConsentService } from '@core/consent';
import { JssPlatformService } from '@core/jss-platform';

import { CognigyClientAnalyticsService } from './cognigy-client/cognigy-client-analytics.service';
import { ConversationalChatEventsService } from './conversational-chat-events.service';
import { ConversationalChatSessionService } from './conversational-chat-session.service';
import { ConversationalChatPersistenceService } from './conversational-chat-persistence.service';

const DEFAULT_TEASER_MESSAGE = 'Vragen? Ik help je graag verder!';
const DEFAULT_WEBCHAT_DELAY = 3000;

declare global {
  interface Window {
    webchat?: WebchatInstance;
    initWebchat: (
      endpoint: string,
      webchatOptions: WebchatOptions
    ) => Promise<WebchatInstance>;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ConversationalChatService {
  readonly #window = inject(WINDOW);
  readonly #config = inject(ENVIRONMENT_CONFIG);

  readonly #platformService = inject(PlatformService);
  readonly #jssPlatformService = inject(JssPlatformService);
  readonly #consentService = inject(ConsentService);
  readonly #deviceInfo = inject(MediaObserverService).deviceInfo();
  readonly #conversationalChatSessionService = inject(
    ConversationalChatSessionService
  );
  readonly #conversationalChatPersistenceService = inject(
    ConversationalChatPersistenceService
  );

  readonly #cognigyClientAnalyticsService = inject(
    CognigyClientAnalyticsService
  );
  readonly #conversationalChatEventService = inject(
    ConversationalChatEventsService
  );
  readonly #cognigyWebchatEndpoint =
    this.#config.cognigyChatbot.webchatEndpoint;

  readonly #logPrefix = 'Cognigy Chatbot - ';

  readonly #loginEncryptedToken: string | null = null;

  readonly isAllowedToInitialize = computed(() => {
    if (!this.#config.cognigyChatbot.enabled) {
      if (isDevMode()) {
        console.warn(this.#logPrefix + 'Chatbot is disabled in configuration');
      }
      return false;
    }
    if (!this.#platformService.isClient()) {
      if (isDevMode()) {
        console.warn(this.#logPrefix + 'Chatbot is not run on the client side');
      }
      return false;
    }
    if (!this.#consentService.consentStatus()) {
      if (isDevMode()) {
        console.warn(this.#logPrefix + 'Consent is not given');
      }
      return false;
    }
    if (this.#jssPlatformService.isEditorActive()) {
      if (isDevMode()) {
        console.warn(
          this.#logPrefix +
            'Chatbot should not run in the sitecore experience editor'
        );
      }
      return false;
    }

    return true;
  });

  initializeWebchat(
    invokedClass?: string,
    webchatDelay = DEFAULT_WEBCHAT_DELAY
  ): void {
    if (!this.isAllowedToInitialize()) {
      return;
    }

    this.#logInitialization(webchatDelay, invokedClass);

    const { sessionId, shouldOpenLastConversation } =
      this.#conversationalChatSessionService.getWebchatState();

    const hasAcceptedTerms =
      this.#conversationalChatPersistenceService.getHasAcceptedTerms();

    const delay = shouldOpenLastConversation ? 0 : webchatDelay;

    setTimeout(async () => {
      await this.#initializeWebchatAsync(
        sessionId,
        hasAcceptedTerms,
        shouldOpenLastConversation,
        invokedClass
      );
    }, delay);
  }

  #logInitialization(webchatDelay: number, invokedClass?: string) {
    if (isDevMode()) {
      console.info(
        `${this.#logPrefix}Initialize Webchat with invokedClass: ${invokedClass} and webchatDelay: ${webchatDelay}`
      );
    }
  }

  async #initializeWebchatAsync(
    sessionId: string,
    hasAcceptedTerms: boolean,
    shouldOpenLastConversation: boolean,
    invokedClass?: string
  ): Promise<void> {
    if (!this.#validateWebchatAvailable()) {
      return;
    }

    const webchat = await this.#createWebchatInstance(sessionId);
    this.#window.webchat = webchat;

    if (hasAcceptedTerms && shouldOpenLastConversation) {
      this.#openWebchat();
    }

    this.#registerWebchatServices(webchat, invokedClass);
  }

  #validateWebchatAvailable(): boolean {
    if (!this.#window.initWebchat) {
      if (isDevMode()) {
        console.error(
          `${this.#logPrefix}The function initWebchat of the Cognigy Webchat script is not available`
        );
      }
      return false;
    }
    return true;
  }

  async #createWebchatInstance(sessionId: string): Promise<WebchatInstance> {
    const webchatOptions: WebchatOptions = {
      sessionId,
      settings: {
        teaserMessage: {
          text: this.#deviceInfo.isMobile ? '' : DEFAULT_TEASER_MESSAGE,
          teaserMessageDelay: 0,
        },
      },
    };

    return this.#window.initWebchat(
      this.#cognigyWebchatEndpoint,
      webchatOptions
    );
  }

  #openWebchat(): void {
    if (!this.#window.webchat) {
      if (isDevMode()) {
        console.error(
          `${this.#logPrefix}The function webchat of the Cognigy Webchat script is not available`
        );
      }
      return;
    }

    if (isDevMode()) {
      console.info(`${this.#logPrefix}Open Webchat`);
    }
    this.#window.webchat.open();

    if (isDevMode()) {
      console.info(`${this.#logPrefix}Start Conversation`);
    }
    this.#window.webchat.startConversation();
  }

  #registerWebchatServices(
    webchat: WebchatInstance,
    invokedClass?: string
  ): void {
    this.#cognigyClientAnalyticsService.registerAnalytics(webchat);
    this.#conversationalChatEventService.registerWebchatSwitchSessionListener();

    const chatbotAnalytics: string[] = [];
    const referrerHref = this.#window.location.href;

    this.#addSourceToChatbotAnalytics(chatbotAnalytics, referrerHref);
    this.#addInvokedClassToChatbotAnalytics(chatbotAnalytics, invokedClass);

    this.#conversationalChatEventService.registerWebchatIncomingMessageListener(
      webchat,
      referrerHref,
      chatbotAnalytics,
      this.#loginEncryptedToken
    );
  }

  #addSourceToChatbotAnalytics(
    chatbotAnalytics: string[],
    referrerHref: string
  ): void {
    // listen for specific URL tags from email campaigns or contact page, chat needs to open automatically if one of these is present in URL
    if (referrerHref.includes('#contact')) {
      chatbotAnalytics.push('source:contact');
    }
  }

  #addInvokedClassToChatbotAnalytics(
    chatbotAnalytics: string[],
    invokedClass?: string
  ): void {
    if (invokedClass) {
      chatbotAnalytics.push('invokedClass:' + invokedClass);
    }
  }
}
